import { APP_ID, isDevMode, NgModule, signal } from '@angular/core';
import { AgrBrowserModule } from '@next/core-lib/browser';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { parseGrainRegistrationEnvironment } from './shared/util/parse-environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

const loadRuntimeConfig = async (): Promise<void> => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseGrainRegistrationEnvironment(config) });
      console.log('Runtime configuration loaded successfully');
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
};

export const preserveAgrSessionSnapshot = (): void => {
  try {
    const search = typeof globalThis === 'undefined' ? '' : (globalThis.location?.search ?? '');
    if (!search) return;
    const urlParams = new URLSearchParams(search);
    let agrSession = urlParams.get('AGR_SESSION');
    // If not found, try parsing encoded parameter names
    if (!agrSession) {
      for (const [key] of urlParams.entries()) {
        if (key.includes('AGR_SESSION')) {
          const parts = decodeURIComponent(key).split('=');
          if (parts.length === 2 && parts[0] === 'AGR_SESSION') {
            agrSession = parts[1];
            break;
          }
        }
      }
    }
    if (agrSession) {
      globalThis.sessionStorage?.setItem('AGR_SESSION_SNAPSHOT', agrSession);
    }
  } catch (e) {
    console.warn('Failed to preserve AGR_SESSION snapshot', e);
  }
};

export const setupRuntimeConfig = loadRuntimeConfig();

@NgModule({
  imports: [AppModule, AgrBrowserModule],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    provideStore(),
    provideEffects(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: false,
    }),
  ],
})
export class AppBrowserModule {}
